import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieLabelRenderProps,
} from 'recharts';
import { useChartAccessibility } from '../../hooks/useChartAccessibility';
import { ChartPatternDefs } from '../accessibility/ChartPatternDefs';
import { AccessibleDataTable } from '../accessibility/AccessibleDataTable';

export interface PieChartDataPoint {
  name: string;
  value: number;
  color?: string;
  [key: string]: string | number | undefined;
}

export interface PieChartProps {
  data: PieChartDataPoint[];
  height?: number | string;
  innerRadius?: number | string;
  outerRadius?: number | string;
  showLegend?: boolean;
  showTooltip?: boolean;
  showLabels?: boolean;
  colors?: string[];
  className?: string;
  /** Called when a slice is clicked. Receives the underlying data row. */
  onSliceClick?: (row: PieChartDataPoint) => void;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
  '#84cc16', // lime-500
];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = (props: PieLabelRenderProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;

  if (typeof cx !== 'number' || typeof cy !== 'number' ||
      typeof midAngle !== 'number' || typeof innerRadius !== 'number' ||
      typeof outerRadius !== 'number' || typeof percent !== 'number') {
    return null;
  }

  if (percent < 0.05) return null; // Don't show label for small slices

  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export function PieChart({
  data,
  height = '100%',
  innerRadius = 0,
  outerRadius = '80%',
  showLegend = true,
  showTooltip = true,
  showLabels = true,
  colors = DEFAULT_COLORS,
  className = '',
  onSliceClick,
}: PieChartProps) {
  const a11y = useChartAccessibility();
  const usePatterns = a11y.usePatternFills;
  const colorAt = (index: number) =>
    a11y.resolveColor(index, data[index]?.color || colors[index % colors.length]);

  return (
    <div className={`w-full h-full flex flex-col ${className}`} style={typeof height === 'number' ? { height } : undefined}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            {usePatterns && ChartPatternDefs({ colors: data.map((_, i) => colorAt(i)) })}
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              dataKey="value"
              label={showLabels ? renderCustomizedLabel : false}
              labelLine={false}
              isAnimationActive={a11y.isAnimationActive}
              animationDuration={a11y.animationDuration}
              onClick={onSliceClick ? (payload: any) => {
                const row = payload && payload.payload ? payload.payload : payload;
                if (row) onSliceClick(row as PieChartDataPoint);
              } : undefined}
              style={onSliceClick ? { cursor: 'pointer' } : undefined}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={usePatterns ? `url(#pattern-${index})` : colorAt(index)}
                />
              ))}
            </Pie>
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: a11y.tooltipBg,
                  border: `1px solid ${a11y.tooltipBorder}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: a11y.tooltipText,
                }}
                labelStyle={{ color: a11y.tooltipText }}
                itemStyle={{ color: a11y.tooltipText }}
              />
            )}
            {showLegend && (
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                formatter={(value) => (
                  <span className="text-sm text-gray-700 dark:text-gray-200">{value}</span>
                )}
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      <AccessibleDataTable
        caption="Pie chart data"
        columns={[{ key: 'name', label: 'Name' }, { key: 'value', label: 'Value' }]}
        data={data}
      />
    </div>
  );
}

// Donut chart is just a PieChart with innerRadius
export function DonutChart(props: Omit<PieChartProps, 'innerRadius'>) {
  return <PieChart {...props} innerRadius="55%" outerRadius="80%" />;
}
